
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Plus, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppointmentForm } from "@/components/appointments/AppointmentForm";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const MobileAppointments = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const navigate = useNavigate();

  const { data: appointments, isLoading } = useQuery({
    queryKey: ["appointments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .order("date", { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const upcomingAppointments = appointments?.filter(
    appointment => new Date(appointment.date) > new Date()
  );

  const pastAppointments = appointments?.filter(
    appointment => new Date(appointment.date) <= new Date()
  );

  // Function to format time from the date string
  const formatAppointmentTime = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'HH:mm');
  };

  return (
    <div className="p-4 pb-20">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold">Mes rendez-vous</h1>
      </div>

      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Nouveau rendez-vous
        </Button>
      </div>

      <AppointmentForm 
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div 
              key={n} 
              className="animate-pulse h-32 rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {upcomingAppointments && upcomingAppointments.length > 0 ? (
            <div>
              <h2 className="text-lg font-medium mb-3">Rendez-vous à venir</h2>
              <div className="space-y-3">
                {upcomingAppointments.map((appointment) => (
                  <div key={appointment.id} className="border rounded-lg p-4 bg-card">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-100 rounded-full p-3">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{appointment.doctor_name}</h3>
                        <p className="text-sm text-muted-foreground">{appointment.specialty}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <div className="bg-blue-50 text-blue-700 text-xs rounded-full px-2 py-1">
                            {format(new Date(appointment.date), 'PPP', { locale: fr })}
                          </div>
                          <div className="bg-blue-50 text-blue-700 text-xs rounded-full px-2 py-1">
                            {formatAppointmentTime(appointment.date)}
                          </div>
                          <div className="bg-gray-100 text-gray-700 text-xs rounded-full px-2 py-1">
                            {appointment.location}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Aucun rendez-vous à venir</p>
            </div>
          )}

          {pastAppointments && pastAppointments.length > 0 && (
            <div>
              <h2 className="text-lg font-medium mb-3">Rendez-vous passés</h2>
              <div className="space-y-3">
                {pastAppointments.map((appointment) => (
                  <div key={appointment.id} className="border rounded-lg p-4 bg-card opacity-75">
                    <div className="flex items-start gap-3">
                      <div className="bg-gray-100 rounded-full p-3">
                        <Calendar className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{appointment.doctor_name}</h3>
                        <p className="text-sm text-muted-foreground">{appointment.specialty}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <div className="bg-gray-100 text-gray-700 text-xs rounded-full px-2 py-1">
                            {format(new Date(appointment.date), 'PPP', { locale: fr })}
                          </div>
                          <div className="bg-gray-100 text-gray-700 text-xs rounded-full px-2 py-1">
                            {formatAppointmentTime(appointment.date)}
                          </div>
                          <div className="bg-gray-100 text-gray-700 text-xs rounded-full px-2 py-1">
                            {appointment.location}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileAppointments;
